<template>

    <div class="w-full mx-auto py-6 px-3">
      <div v-if="!isLoading" class="practice">
  
        <!-- Breadcrumbs -->
        <nav id='practice-nav' class="flex px-1 lg:px-7 justify-between items-center" aria-label="Breadcrumb">
          <div class="flex items-center">
            <div @click="this.openOnExitModal()" class="text-sm font-medium text-warm-gray-500 hover:text-warm-gray-600 flex items-center cursor-pointer" aria-current="page">
              <LogoutIcon class="flex-shrink-0 h-6 w-6" aria-hidden="true" />
            </div>
          </div>
          <div class="text-sm font-medium text-warm-gray-500">
            <span>Practice Exam for {{ this.subtopic_name }}</span>
          </div>
          <!-- FAQ Menu -->
          <button @click="isSoundOff = !isSoundOff" class="text-warm-gray-500">
            <VolumeOffIcon v-if="isSoundOff" class="h-5 w-5" aria-hidden="true" />
            <VolumeUpIcon v-else class="h-5 w-5" aria-hidden="true" />
          </button>
        </nav>
  
        <!-- Main Content -->
        <main v-if="questionnaire && !isQuestionExplorerShown" class="max-w-full mt-6 mx-auto px-1 sm:py-8 sm:px-6 lg:max-w-7xl lg:px-8 space-y-4 mb-28">
          <!-- Stats -->
          <div id="stats" class="dark:border-true-gray-700 min-h-60 rounded-md mb-4 flex justify-between">
            <div id="scroll-top-target-div" class="flex dark:text-warm-gray-500 text-warm-gray-700  text-md items-center">
              <FlagIcon class="w-5 h-5 mr-1" />
              <p class="hidden sm:block ml-2 font-medium dark:text-warm-gray-500 text-warm-gray-700">
                Question {{ exam.index + 1 }} out of {{ questionnaire.length }}
              </p>
              <p class="block sm:hidden ml-2 font-medium dark:text-warm-gray-500 text-warm-gray-700">
                {{ this.exam.index + 1 }} of {{ questionnaire.length }}
              </p>
            </div>
            <div class="flex text-warm-gray-800 text-md items-center">
              <div class="ml-2 font-medium text-warm-gray-800 flex items-center">
                <div class="flex items-center mr-3 text-green-500">
                  <CheckCircleIcon class="ml-1 w-5 h-5 mr-1" />
                  {{ this.countCorrectAnswers() }}
                  <span class="hidden sm:block ml-1">Correct</span>
                </div>
                <div class="flex items-center text-red-500">
                  <XCircleIcon class="ml-1 w-5 h-5 mr-1" />
                  {{ this.countIncorrectAnswers() }}
                  <span class="hidden sm:block ml-1"> Wrong</span>
                </div>
              </div>
            </div>
          </div>
  
          <!-- Question -->
          <div class="select-none text-left text-warm-gray-900 dark:text-warm-gray-300 rounded-md -space-y-px p-6 border-2 border-dashed border-warm-gray-300 dark:border-true-gray-700 whitespace-pre-line" >
            <div class="quill-content" v-html="questionnaire[exam.index].questionHtml"></div>
            <img v-if="questionnaire[exam.index].hasImage" :src="questionnaire[exam.index].imageUrl" class="lg:max-w-24 rounded-xl mx-auto my-2 mt-4">
          </div>
          
          <!-- Choices -->
          <div id="choices" class="select-none mt-4">
            <ul class="text-warm-gray-800 dark:text-warm-gray-400 text-left rounded-md shadow-sm -space-y-px">
              <li v-for="(choice, idX) in questionnaire[exam.index].choices" :key="`choice-${idX}`">
                <button
                  :disabled="exam.answers[exam.index].answer != null"
                  @click="reveal(idX)"
                  :class="[ showAnswers(idX), showSelected(idX), setBorder(idX), 
                  'flex justify-between border border-warm-gray-300 dark:border-true-gray-700 relative shadow-sm px-5 py-4 cursor-pointer text-left focus:outline-0 w-full disabled:cursor-auto' ]"
                >
                  <!-- <div class="w-full">{{ choice.choice }}</div> -->
                  <span v-html="choice.choiceHtml"></span>
                  <div v-if="exam.answers[exam.index].answer != null">
                    <span v-if="choice.id == questionnaire[exam.index].answer">
                      <CheckCircleIcon class="h-5 w-5" />
                    </span>
                    <span v-else>
                      <XCircleIcon class="h-5 w-5" />
                    </span>
                  </div>
                </button>
              </li>
            </ul>
          </div>
  
          <!-- Action Buttons -->
          <nav class="mt-6 flex flex-col sm:flex-row justify-between">
            <button
              :disabled="exam.index <= 0"
              v-on:click="exam.index > 0 ? exam.index-- : null, resetExplanations(), scrollBackToTop()"
              class="order-last sm:order-first my-2 disabled:opacity-50 w-full sm:w-auto space-x-4 flex flex-inline justify-center items-center disabled:cursor-auto p-4 sm:px-5 border border-warm-gray-300 dark:border-true-gray-70shadow-sm font-medium rounded-md text-warm-gray-700 dark:text-warm-gray-400 bg-white dark:bg-true-gray-800 hover:bg-warm-gray-100 dark:hover:bg-true-gray-600 focus:outline-0 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <ChevronDoubleLeftIcon class="h-5 w-5 dark:text-warm-gray-400 text-warm-gray-500 mr-1"/>
              Previous
            </button>
            <button
              v-if="!isOnlyOneUnansweredLeft()"
              :disabled="exam.answers[exam.index].answer != null"
              v-on:click="skip(exam.index), scrollBackToTop()"
              class="my-2 disabled:opacity-50 w-full sm:w-auto space-x-2 flex flex-inline justify-center items-center disabled:cursor-auto p-4 sm:px-5 border border-warm-gray-300 dark:border-true-gray-700 shadow-sm font-medium rounded-md text-warm-gray-700 dark:text-warm-gray-400 bg-white dark:bg-true-gray-800 hover:bg-warm-gray-100 dark:hover:bg-true-gray-600 focus:outline-0 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Skip<span class="ml-1 hidden sm:block"> This</span>
              <SwitchHorizontalIcon class="h-5 w-5 dark:text-warm-gray-400 text-warm-gray-500 ml-1"/>
            </button>
            <button
              
              v-on:click="this.isQuestionExplorerShown = true"
              class="my-2 disabled:opacity-50 w-full sm:w-auto space-x-2 flex flex-inline justify-center items-center disabled:cursor-auto p-4 sm:px-5  border border-warm-gray-300 dark:border-true-gray-700 shadow-sm font-medium rounded-md text-warm-gray-700 dark:text-warm-gray-400 bg-white dark:bg-true-gray-800 hover:bg-warm-gray-100 dark:hover:bg-true-gray-600 focus:outline-0 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Jump<span class="ml-1 hidden sm:block">To</span>
              <UploadIcon class="h-5 w-5 dark:text-warm-gray-400 text-warm-gray-500 ml-1"/>
            </button>
            <button
              v-if="!isTheRemainingQuestion()"
              :disabled="exam.answers[exam.index].answer == null"
              v-on:click="skip(exam.index), resetExplanations(), scrollBackToTop()"
              class="order-first sm:order-last my-2 disabled:opacity-50 w-full sm:w-auto space-x-4 flex flex-inline justify-center items-center disabled:cursor-auto p-4 sm:px-5 border border-warm-gray-300 dark:border-true-gray-700 shadow-sm font-medium rounded-md text-warm-gray-700 dark:text-warm-gray-400 bg-white dark:bg-true-gray-800 hover:bg-warm-gray-100 dark:hover:bg-true-gray-600 focus:outline-0 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Next
              <ChevronDoubleRightIcon class="h-5 w-5 dark:text-warm-gray-400 text-warm-gray-500 ml-1"/>
            </button>
            <button
              v-if="isTheRemainingQuestion()"
              :disabled="exam.answers[exam.index].answer == null"
              v-on:click="completePractice()"
              class="order-first sm:order-last my-2 disabled:opacity-50 w-full sm:w-auto space-x-4 flex flex-inline justify-center items-center disabled:cursor-auto p-4 sm:px-5 border border-warm-gray-300 dark:border-true-gray-700 shadow-sm font-medium rounded-md text-white bg-purple-700 hover:bg-purple-800 focus:outline-0 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Complete Practice Exam
            </button>
          </nav>
  
          <div v-if="exam.answers[exam.index].answer != null" class="flex flex-col justify-center items-center mt-12">
          <!-- <div v-if="false" class="flex flex-col justify-center items-center mt-12"> -->
            
  
  
            <!-- <div v-if="answerExplanation" class="my-4 mx-auto flex max-w-screen-sm items-center justify-center">
              <div class="h-auto w-full rounded-md bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 p-1">
                <div class="flex h-full w-full items-center justify-center bg-gray-800 back px-4 py-4">
                  <div class="text-sm text-white space-y-4">
                    <p v-for="parag,i  in answerExplanationArr" :key="`answer-explanation-${i}`">
                      {{ parag }}
                    </p>
                  </div>
                </div>
              </div>
            </div> -->
  
            <!-- Show explanation -->
            <div v-if="this.explanation && this.explanation.id != null" class="my-4 mx-auto flex flex-col max-w-screen-sm items-center justify-center">
              <div class="h-auto w-full rounded-md bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 p-1">
                <div class="flex h-full w-full items-center justify-center bg-true-gray-800 back px-4 py-4">
                  <div class="text-sm text-true-gray-400 space-y-4">
  
                    <template v-if="explanation.data.textHtml">
                      <div v-html="explanation.data.textHtml"></div>
                    </template>
                    <template v-else>
                      <p v-for="parag,i in formatExplanationForViewing(explanation.data.text)" :key="`answer-explanation-${i}`">
                        {{ parag }}
                      </p>
                    </template>
  
                  </div>
                </div>
              </div>

              <!-- votes -->
              <div class="mt-2 text-xs dark:text-gray-400 text-gray-700 py-1 px-2 flex space-x-3">
                <span>Does the answer above make sense? Help us improve:</span>
                <button @click="sendFeedBack('upvote')" :disabled="this.alreadyVoted" class="flex flex-inline space-x-1 disabled:cursor-default disabled:opacity-50 hover:opacity-50 transition-all">
                  <svg v-if="this.alreadyVoted && this.voteType == 'upvote'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
                    <path d="M7.493 18.75c-.425 0-.82-.236-.975-.632A7.48 7.48 0 016 15.375c0-1.75.599-3.358 1.602-4.634.151-.192.373-.309.6-.397.473-.183.89-.514 1.212-.924a9.042 9.042 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75 2.25 2.25 0 012.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H14.23c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23h-.777zM2.331 10.977a11.969 11.969 0 00-.831 4.398 12 12 0 00.52 3.507c.26.85 1.084 1.368 1.973 1.368H4.9c.445 0 .72-.498.523-.898a8.963 8.963 0 01-.924-3.977c0-1.708.476-3.305 1.302-4.666.245-.403-.028-.959-.5-.959H4.25c-.832 0-1.612.453-1.918 1.227z" />
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6.633 10.5c.806 0 1.533-.446 2.031-1.08a9.041 9.041 0 012.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 00.322-1.672V3a.75.75 0 01.75-.75A2.25 2.25 0 0116.5 4.5c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 01-2.649 7.521c-.388.482-.987.729-1.605.729H13.48c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 00-1.423-.23H5.904M14.25 9h2.25M5.904 18.75c.083.205.173.405.27.602.197.4-.078.898-.523.898h-.908c-.889 0-1.713-.518-1.972-1.368a12 12 0 01-.521-3.507c0-1.553.295-3.036.831-4.398C3.387 10.203 4.167 9.75 5 9.75h1.053c.472 0 .745.556.5.96a8.958 8.958 0 00-1.302 4.665c0 1.194.232 2.333.654 3.375z" />
                  </svg>
                  <span>{{ this.explanation.data.upvotes }}</span>
                </button>
                <button @click="sendFeedBack('downvote')" :disabled="this.alreadyVoted" class="flex flex-inline space-x-1  disabled:cursor-default disabled:opacity-50 hover:opacity-50 transition-all">
                  <svg v-if="this.alreadyVoted && this.voteType == 'downvote'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
                    <path d="M15.73 5.25h1.035A7.465 7.465 0 0118 9.375a7.465 7.465 0 01-1.235 4.125h-.148c-.806 0-1.534.446-2.031 1.08a9.04 9.04 0 01-2.861 2.4c-.723.384-1.35.956-1.653 1.715a4.498 4.498 0 00-.322 1.672V21a.75.75 0 01-.75.75 2.25 2.25 0 01-2.25-2.25c0-1.152.26-2.243.723-3.218C7.74 15.724 7.366 15 6.748 15H3.622c-1.026 0-1.945-.694-2.054-1.715A12.134 12.134 0 011.5 12c0-2.848.992-5.464 2.649-7.521.388-.482.987-.729 1.605-.729H9.77a4.5 4.5 0 011.423.23l3.114 1.04a4.5 4.5 0 001.423.23zM21.669 13.773c.536-1.362.831-2.845.831-4.398 0-1.22-.182-2.398-.52-3.507-.26-.85-1.084-1.368-1.973-1.368H19.1c-.445 0-.72.498-.523.898.591 1.2.924 2.55.924 3.977a8.959 8.959 0 01-1.302 4.666c-.245.403.028.959.5.959h1.053c.832 0 1.612-.453 1.918-1.227z" />
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 15h2.25m8.024-9.75c.011.05.028.1.052.148.591 1.2.924 2.55.924 3.977a8.96 8.96 0 01-.999 4.125m.023-8.25c-.076-.365.183-.75.575-.75h.908c.889 0 1.713.518 1.972 1.368.339 1.11.521 2.287.521 3.507 0 1.553-.295 3.036-.831 4.398C20.613 14.547 19.833 15 19 15h-1.053c-.472 0-.745-.556-.5-.96a8.95 8.95 0 00.303-.54m.023-8.25H16.48a4.5 4.5 0 01-1.423-.23l-3.114-1.04a4.5 4.5 0 00-1.423-.23H6.504c-.618 0-1.217.247-1.605.729A11.95 11.95 0 002.25 12c0 .434.023.863.068 1.285C2.427 14.306 3.346 15 4.372 15h3.126c.618 0 .991.724.725 1.282A7.471 7.471 0 007.5 19.5a2.25 2.25 0 002.25 2.25.75.75 0 00.75-.75v-.633c0-.573.11-1.14.322-1.672.304-.76.93-1.33 1.653-1.715a9.04 9.04 0 002.86-2.4c.498-.634 1.226-1.08 2.032-1.08h.384" />
                  </svg>
                  <span>{{ this.explanation.data.downvotes }}</span>
                </button>
              </div>
            </div>
            <div v-if="this.showNoExplanation">
              <div class="my-4 mx-auto flex flex-col max-w-screen-sm items-center justify-center">
                <div class="h-auto w-full rounded-md bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 p-1">
                  <div class="flex h-full w-full items-center justify-center bg-true-gray-800 px-4 py-4 space-x-4">
                    <div class="text-sm text-true-gray-400">
                      <p>No explanation available for this question. You can click <a href="https://forms.gle/TdQmRv48Xy9ZKutH7" target="_blank" class="underline hover:opacity-70">here</a> to submit a bug report.</p>
                    </div>
                    <button @click="showNoExplanation = false" class="hover:opacity-75 transition-all text-true-gray-400">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
  
            <!-- <div class="flex justify-center text-sm text-warm-gray-700 dark:text-warm-gray-300 items-center space-x-2">
              <div>Powered by</div>
              <div class="h-4 -mt-1">
                <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 118 32" class="h-5" data-new="" aria-hidden="true" data-v-069f367b=""><g fill="currentColor" data-v-069f367b=""><path d="m36.7 15.4c0 5.2 3.4 8.9 8 8.9s8-3.6 8-8.9-3.4-8.9-8-8.9-8 3.7-8 8.9zm13 0c0 3.7-2 6.2-4.9 6.2s-4.9-2.4-4.9-6.2 2-6.2 4.9-6.2 4.9 2.4 4.9 6.2z"></path><path d="m61.4 24.3c3.5 0 5.5-3 5.5-6.6s-2-6.6-5.5-6.6c-1.6 0-2.8.6-3.6 1.6v-1.3h-2.9v16.9h2.9v-5.6c.8.9 2 1.6 3.6 1.6zm-3.7-6.9c0-2.4 1.3-3.7 3.1-3.7 2.1 0 3.2 1.6 3.2 4s-1.1 4-3.2 4c-1.8 0-3.1-1.3-3.1-3.6z"></path><path d="m74.8 24.3c2.5 0 4.5-1.3 5.4-3.5l-2.5-.9c-.4 1.3-1.5 2-2.9 2-1.8 0-3.1-1.3-3.4-3.5h8.8v-1c0-3.5-1.9-6.2-5.6-6.2-3.5 0-6 2.9-6 6.6 0 3.8 2.6 6.5 6.2 6.5zm-.2-10.7c1.8 0 2.7 1.2 2.7 2.6h-5.8c.5-1.7 1.6-2.6 3.1-2.6z"></path><path d="m82.4 24h2.9v-7.4c0-1.8 1.3-2.8 2.6-2.8 1.6 0 2.2 1.1 2.2 2.7v7.5h2.9v-8.3c0-2.7-1.6-4.5-4.2-4.5-1.6 0-2.8.7-3.5 1.6v-1.3h-2.9z"></path><path d="m101.4 6.8-6.5 17.2h3l1.5-3.9h7.4l1.5 3.9h3.1l-6.5-17.2zm1.7 3.4 2.7 7.2h-5.4z"></path><path d="m116.4 6.8h-3.1v17.2h3.1z"></path><path class="a-icon--logo-text-logo" d="m29.7 13.1c.4-1.1.5-2.2.4-3.3s-.5-2.2-1-3.2c-.9-1.5-2.2-2.7-3.7-3.4-1.6-.7-3.3-.9-5-.5-.8-.8-1.7-1.5-2.7-2s-2.2-.7-3.3-.7c-1.7 0-3.4.5-4.8 1.5s-2.4 2.4-2.9 4c-1.2.3-2.2.8-3.2 1.4-.9.7-1.6 1.6-2.2 2.5-.9 1.5-1.2 3.2-1 4.9s.9 3.3 2 4.6c-.4 1.1-.5 2.2-.4 3.3s.5 2.2 1 3.2c.9 1.5 2.2 2.7 3.7 3.4 1.6.7 3.3.9 5 .5.8.8 1.7 1.5 2.7 2s2.2.7 3.3.7c1.7 0 3.4-.5 4.8-1.5s2.4-2.4 2.9-4c1.1-.2 2.2-.7 3.1-1.4s1.7-1.5 2.2-2.5c.9-1.5 1.2-3.2 1-4.9s-.8-3.3-1.9-4.6zm-12 16.8c-1.6 0-2.8-.5-3.9-1.4 0 0 .1-.1.2-.1l6.4-3.7c.2-.1.3-.2.4-.4s.1-.3.1-.5v-9l2.7 1.6v7.4c.1 3.5-2.7 6.1-5.9 6.1zm-12.9-5.5c-.7-1.2-1-2.6-.7-4 0 0 .1.1.2.1l6.4 3.7c.2.1.3.1.5.1s.4 0 .5-.1l7.8-4.5v3.1l-6.5 3.8c-1.4.8-3 1-4.5.6-1.6-.4-2.9-1.4-3.7-2.8zm-1.7-13.9c.7-1.2 1.8-2.1 3.1-2.6v.2 7.4c0 .2 0 .4.1.5.1.2.2.3.4.4l7.8 4.5-2.7 1.6-6.4-3.7c-1.4-.8-2.4-2.1-2.8-3.6s-.3-3.3.5-4.7zm22.1 5.1-7.8-4.5 2.7-1.6 6.4 3.7c1 .6 1.8 1.4 2.3 2.4s.8 2.1.7 3.3c-.1 1.1-.5 2.2-1.2 3.1s-1.6 1.6-2.7 2v-7.6c0-.2 0-.4-.1-.5 0 0-.1-.2-.3-.3zm2.7-4s-.1-.1-.2-.1l-6.4-3.7c-.2-.1-.3-.1-.5-.1s-.4 0-.5.1l-7.8 4.5v-3.1l6.5-3.8c1-.6 2.1-.8 3.3-.8 1.1 0 2.2.4 3.2 1.1.9.7 1.7 1.6 2.1 2.6s.5 2.2.3 3.3zm-16.8 5.6-2.7-1.6v-7.5c0-1.1.3-2.3.9-3.2.6-1 1.5-1.7 2.5-2.2s2.2-.7 3.3-.5c1.1.1 2.2.6 3.1 1.3 0 0-.1.1-.2.1l-6.4 3.7c-.2.1-.3.2-.4.4s-.1.3-.1.5zm1.4-3.2 3.5-2 3.5 2v4l-3.5 2-3.5-2z"></path></g></svg>
              </div>
            </div> -->
          </div>
        </main>
  
        <section id="questionExplorer" v-if="isQuestionExplorerShown" class="flex flex-col items-end mt-8 mb-28">
          <button @click="closeQExplorer()" class="my-2 flex hover:opacity-75 items-center text-indigo-500">
            <XIcon class="h-5 w-5  mt-0.5 mr-1"/>
            <span>Cancel and Close</span>
            
          </button>
          <div class="w-full">
            <ul>
              <li v-for="(item, index) in exam.questionnaire" :key="`question-explorer-${index}`" class="">
                <button 
                  @click="jump(exam.index, index)" :class="[(exam.answers[index].answer != null) ? 'opacity-50':'','w-full my-2 space-x-2 flex flex-inline justify-between items-center disabled:cursor-auto p-4 sm:px-5 border border-warm-gray-300 dark:border-true-gray-700 shadow-sm font-medium rounded-md text-warm-gray-700 dark:text-warm-gray-400 bg-white dark:bg-true-gray-800']">
                  <div class="flex text-left space-x-2 items-center">
                    <span v-if="exam.answers[index].answer != null">
                      <span v-if="exam.answers[index].answer == exam.questionnaire[index].answer">
                        <CheckCircleIcon class="text-green-500 h-5 w-5" />
                      </span>
                      <span v-else>
                        <XCircleIcon class="text-red-500 h-5 w-5" />
                      </span>
                    </span>
                    <span v-html="item.questionHtml"></span>
                    <!-- <span class="text-left">{{ item.question }}</span> -->
                  </div>
                  <span class="ml-3">
                    <ChevronDoubleRightIcon v-if="exam.answers[index].answer == null" class="h-5 w-5 dark:text-warm-gray-400 text-warm-gray-500 ml-1"/>
                  </span>
                </button>
                
              </li>
            </ul>
          </div>
        </section>
  
        <!-- Popup: On Exit -->
        <TransitionRoot as="template" :show="isOnExitOpen">
          <Dialog as="div" auto-reopen="true" class="fixed z-10 inset-0 overflow-y-auto" @close="isOnExitOpen = false">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <DialogOverlay class="fixed inset-0 dark:bg-true-gray-900 dark:bg-opacity-75 bg-warm-gray-900 bg-opacity-75" />
              </TransitionChild>
              <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                &#8203;
              </span>
              <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                <div class="inline-block align-bottom dark:bg-true-gray-800 bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                  <div class="hidden sm:block absolute top-0 right-0 pt-4 pr-4"></div>
                  <div class="sm:flex sm:items-start">
                    <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                      <ExclamationIcon class="h-6 w-6 text-yellow-600" aria-hidden="true"/>
                    </div>
                    <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <DialogTitle as="h3" class="text-lg leading-6 font-medium dark:text-gray-200 text-warm-gray-900">
                        Before you go...
                      </DialogTitle>
                      <div class="mt-2">
                        <p class="text-sm text-warm-gray-500 dark:text-warm-gray-400">
                          Please note that your progress in this Practice Exam will
                          be saved. You can always come back later to continue where
                          you left off or start a new practice session.
                        </p>
                        <p class="text-sm text-gray-500 dark:text-warm-gray-400 max-w-sm mt-4">
                          Are you sure you want to proceed?
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      @click="this.exitPractice()"
                      type="button"
                      class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-0 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Exit and Save
                    </button>
                    <button
                      @click="isOnExitOpen = false, exam.status = 'started'"
                      type="button"
                      class="mt-3 w-full inline-flex justify-center text-warm-gray-700 dark:text-warm-gray-400 bg-white dark:bg-true-gray-700 hover:bg-warm-gray-100 dark:hover:bg-true-gray-600 rounded-md border dark:border-warm-gray-600 border-warm-gray-300 shadow-sm px-4 py-2 text-base font-medium focus:outline-0 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </TransitionChild>
            </div>
          </Dialog>
        </TransitionRoot>
  
        <!-- Popup: On Complete -->
        <TransitionRoot appear :show="isOnCompleteOpen" as="template">
          <Dialog as="div">
            <div class="fixed inset-0 z-10 overflow-y-auto">
              <div class="min-h-screen px-4 text-center">
                <TransitionChild as="template" enter="duration-300 ease-out" enter-from="opacity-0" enter-to="opacity-100" leave="duration-200 ease-in" leave-from="opacity-100" leave-to="opacity-0">
                  <DialogOverlay class="fixed inset-0 dark:bg-true-gray-900 dark:bg-opacity-75 bg-warm-gray-900 bg-opacity-75" />
                </TransitionChild>
                <span class="inline-block h-screen align-middle" aria-hidden="true">
                  &#8203;
                </span>
                <TransitionChild as="template" enter="duration-300 ease-out" enter-from="opacity-0 scale-95" enter-to="opacity-100 scale-100" leave="duration-200 ease-in" leave-from="opacity-100 scale-100" leave-to="opacity-0 scale-95">
                  <div class="text-center inline-block w-full max-w-xl p-8 my-8 overflow-hidden align-middle transition-all transform bg-white dark:bg-true-gray-800 shadow-xl rounded-2xl">
                    <DialogTitle as="h3" class=" text-sm uppercase font-semibold leading-6 text-warm-gray-500 dark:text-warm-gray-200 text-center">
                      Practice Exam Results
                    </DialogTitle>
                    <div class="mt-2">
                      <div class="text-3xl font-bold text-warm-gray-700 dark:text-warm-gray-200 mt-8">
                        <p v-if="this.exam.score == 100">Highly impressive! 🥳🔥</p>
                        <p v-else-if="this.exam.result == 'passed'">Wow, you’re good! 😲👍🏼</p>
                        <p v-else>Not quite there yet. 😉👌🏼</p>
                      </div>
                      <p class="text-lg dark:text-warm-gray-200 text-warm-gray-700 mt-1 mb-8">
                        <!-- You got {{this.countCorrectAnswers()}} correct answers out of {{this.questionnaire.length}} items in {{ showTime('hour') }} {{ showTime('minute') }} {{ showTime('second') }} -->
                        You got {{ this.countCorrectAnswers() }} correct answers out of {{ this.questionnaire.length }} items.
                      </p>
                      <div class="text-sm dark:text-warm-gray-400 text-warm-gray-500 max-w-sm mx-auto">
                        <p v-if="this.exam.score == 100">
                          You’ve accomplished something that most people can’t even attain. Congratulations and well done!
                        </p>
                        <p v-else-if="this.exam.result == 'passed'">
                          You’re doing really well. Your score shows that you’re on the right track. Keep it up and don’t stop. Great job!
                        </p>
                        <p v-else>
                          We know the feeling. But practice makes perfect, right? Don’t worry, you’ll get there. Now try again. 
                        </p>
                      </div>
                      <div class="bg-gray-50 overflow-hidden rounded-lg text-sm text-warm-gray-500 text-left mt-8 max-w-lg">
                        <div class="px-4 py-5 sm:p-5 flex space-x-2 items-start">
                          <div>
                            <LightBulbIcon class="text-yellow-400 w-5 h-5" />
                          </div>
                          <p class="text-yellow-700">
                            <strong>Did You Know?</strong> {{tips[Math.floor(Math.random() * this.tips.length)]}}
                          </p>
                        </div>
                      </div>
                      <div class="mt-8 sm:mt-6">
                        <button @click="this.exitPractice()" type="button" class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-0 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:col-start-2 sm:text-sm">
                          Continue
                        </button>
                      </div>
                    </div>
                  </div>
                </TransitionChild>
              </div>
            </div>
          </Dialog>
        </TransitionRoot>
  
        </div>
        <!-- Show loading screen -->
        <div v-else>
        <div class="max-w-full mt-12 mx-auto px-1 sm:py-8 sm:px-6 lg:max-w-7xl lg:px-8 space-y-4">
          <div id="stats" class="dark:border-true-gray-700 min-h-60 rounded-md mb-4 flex justify-between">
            <div class="flex flex-inline">
              <FlagIcon class="h-5 w-5 dark:text-warm-gray-400 text-warm-gray-800 mr-2"/>
              <div class="dark:bg-true-gray-800 bg-warm-gray-200 h-5 rounded-md w-32 animate-pulse"></div>
            </div>
            <div class="flex text-warm-gray-800 text-md items-center">
              <div class="ml-2 font-medium text-warm-gray-800 flex items-center">
                <div class="flex items-center mr-3 text-green-500">
                  <CheckCircleIcon class="ml-1 w-5 h-5 mr-2" />
                  <div class="dark:bg-true-gray-800 bg-warm-gray-300 rounded-md h-5 w-8 sm:w-28 animate-pulse"></div>
                </div>
                <div class="flex items-center text-red-500">
                  <XCircleIcon class="ml-1 w-5 h-5 mr-2" />
                  <div class="dark:bg-true-gray-800 bg-warm-gray-300 rounded-md h-5 w-8 sm:w-28 animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
          <div class="rounded-md -space-y-px p-6 border-2 border-dashed border-warm-gray-300 dark:border-true-gray-700">
            <div class="dark:bg-true-gray-800 bg-warm-gray-300 h-5 rounded-md w-full animate-pulse"></div>
          </div>
          <div id="choices" class="mt-4">
            <ul class="text-warm-gray-800 dark:text-warm-gray-400 text-left rounded-md shadow-sm -space-y-px animate-pulse">
              <li>
                <button class="rounded-tl-md rounded-tr-md border border-warm-gray-300 dark:border-true-gray-700 bg-white dark:bg-true-gray-800 relative shadow-sm hover:bg-warm-gray-100 dark:hover:bg-true-gray-600 px-5 py-4 cursor-pointer text-left focus:outline-0 w-full">
                  <div class="h-4"></div>
                </button>
              </li>
              <li>
                <button class="border border-warm-gray-300 dark:border-true-gray-700 bg-white dark:bg-true-gray-800 relative shadow-sm hover:bg-warm-gray-100 dark:hover:bg-true-gray-600 px-5 py-4 cursor-pointer text-left focus:outline-0 w-full">
                  <div class="h-4"></div>
                </button>
              </li>
              <li>
                <button class="rounded-bl-md rounded-br-md border border-warm-gray-300 dark:border-true-gray-700 bg-white dark:bg-true-gray-800 relative shadow-sm hover:bg-warm-gray-100 dark:hover:bg-true-gray-600 px-5 py-4 cursor-pointer text-left focus:outline-0 w-full">
                  <div class="h-4"></div>
                </button>
              </li>
            </ul>
          </div>
          <nav class="mt-6 flex justify-between space-x-4">
            <button class="disabled:opacity-50 w-full sm:w-auto space-x-4 flex flex-inline justify-center items-center disabled:pointer-events-none p-4 sm:px-5 border border-warm-gray-300 dark:border-true-gray-700 shadow-sm font-medium rounded-md text-warm-gray-700 dark:text-warm-gray-400 bg-white dark:bg-true-gray-800 hover:bg-warm-gray-100 dark:hover:bg-true-gray-600 focus:outline-0 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 animate-pulse">
              <div class="w-full sm:w-12 h-4"></div>
            </button>
            <button class="disabled:opacity-50 w-full sm:w-auto space-x-4 flex flex-inline justify-center items-center disabled:pointer-events-none p-4 sm:px-5 border border-warm-gray-300 dark:border-true-gray-700 shadow-sm font-medium rounded-md text-warm-gray-700 dark:text-warm-gray-400 bg-white dark:bg-true-gray-800 hover:bg-warm-gray-100 dark:hover:bg-true-gray-600 focus:outline-0 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 animate-pulse">
              <div class="w-full sm:w-12 h-4"></div>
            </button>
          </nav>
        </div>
        </div>
    </div>
    
  </template>
  
  <script>
  // Import firebase dependencies
  
  import correctSound from '@/assets/audio/correct.mp3'
  import wrongSound from '@/assets/audio/wrong.mp3'
  import passedSound from '@/assets/audio/passed.mp3'
  import failedSound from '@/assets/audio/failed.mp3'
  
  import firebase from "@/firebase/config";
  import "firebase/auth";
  import "firebase/firestore";
  import { upsertExplanationByQuestionId } from "@/firebase/explanationsController";
  import axios from "axios";
  import { attachLeaveGuard, detachLeaveGuard } from "@/utils/leaveGuard";
  const db = firebase.firestore();
  
  const apiKey = process.env.VUE_APP_OPENAI_API_KEY;
  const client = axios.create({
      headers: {
        Authorization: "Bearer " + apiKey,
      },
  });
  
  
  
  // Import UI dependencies
  import { ref } from "vue";
  import { Menu, MenuButton, MenuItem, MenuItems, TransitionRoot, TransitionChild, Dialog, DialogOverlay, DialogTitle } from "@headlessui/vue";
  import { SwitchHorizontalIcon, XIcon, UploadIcon, DownloadIcon, CheckCircleIcon, XCircleIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon, LogoutIcon, FlagIcon, ExclamationIcon } from "@heroicons/vue/outline";
  import { VolumeUpIcon, VolumeOffIcon, QuestionMarkCircleIcon, LightBulbIcon } from "@heroicons/vue/solid";
  
  export default {
    components: {
      XIcon,
      UploadIcon,
      DownloadIcon,
      CheckCircleIcon,
      XCircleIcon,
      ChevronDoubleLeftIcon,
      ChevronDoubleRightIcon,
      QuestionMarkCircleIcon,
      LightBulbIcon,
      LogoutIcon,
      FlagIcon,
      ExclamationIcon,
      TransitionRoot,
      TransitionChild,
      Dialog,
      DialogOverlay,
      DialogTitle,
      Menu,
      MenuButton,
      MenuItem,
      MenuItems,
      SwitchHorizontalIcon,
      VolumeUpIcon, 
      VolumeOffIcon
  },
    setup() {
      const alreadyVoted = ref(false);
      const voteType = ref('');
      const isOnCompleteOpen = ref(false);
      const isOnExitOpen = ref(false);
      // const answerExplanation = ref('');
      // const answerExplanationArr = ref([]);
      const explanation = ref({});
      const isWaitingForExplanation = ref(false);
  
      return {
        isOnCompleteOpen,
        isOnExitOpen,
        isWaitingForExplanation,
        // answerExplanation,
        // answerExplanationArr,
        explanation,
        alreadyVoted,
        voteType,
        openOnCompleteModal() {
          isOnCompleteOpen.value = true;
        },
        closeOnExitModal() {
          isOnExitOpen.value = false;
        },
        openOnExitModal() {
          isOnExitOpen.value = true;
        },
      };
    },
    data() {
      return {
        isSoundOff: false,
        correctAudio: null,
        wrongAudio: null,
        passedAudio: null,
        failedAudio: null,
        subtopic: null,
        subtopic_name: null, // For display. @TODO: please optimise later
        questionnaire: null,
        exam: null,
        isLoading: true,
        showPopup: false,
        isQuestionExplorerShown: false,
        // @TODO: Move this to DB
        tips: [
          "The CAAP Knowledge Test are given in various exam sets. Meaning, your exam may differ from what your friend is taking, even if you’re doing the test for the same subject.",
          "CAAP exams have time limits. Typically, the number of items in a subject is also the number of minutes allotted to complete the exam. For example, 30 items means 30 minutes. ",
          "One of the creators of this app has managed to pass all the CAAP Knowledge Test for AMT subjects in only one day. 7 subjects in 1 day. It’s possible.",
          "The passing score for the actual exam is 70% across all subjects. Want to know if you’re ready? Our Mock Exams follows the same passing rates so always give it a try.",
          "Skilltech Reviewer’s concepts and features are the results of in-depth research into real life experiences of Filipino AMT and AVT exam takers. ",
          "This reviewer has been created with the inputs of actual CAAP exam passers. Through hundreds of interviews, surveys, and statistics, we’ve managed to collect the best ideas from the Filipino aviation community. ",
          "In the Philippines, your CAAP Airmen License will be required first before a company promotes you to a Certifying Staff. No license, no promotion. That’s why it’s always better to have it ready.",
          "The CAAP Airmen License is one of the most frequently required credentials when applying for jobs abroad. Job agencies in the Philippines usually include your license in their qualification list.",
          "Our survey shows that the majority of previous exam takers consider Air Law and Airworthiness to be the most difficult subject and Human Performance to be the easiest.",
          "We are BSAMT but we started our OJT right after we got our Associate’s degree. After completing that, we took the CAAP exams. By the time we’re in 3rd year in college, we already have our CAAP AMT licenses.",
        ],
        loaded: [],
        showNoExplanation: false
      };
    },
  
    methods: {
      scrollBackToTop () {
          const element = document.getElementById('scroll-top-target-div');
          if (element) {
              element.scrollIntoView({ behavior: 'smooth' });
          }
      },
      skip(toIndex){
  
        // console.log(toIndex)
        this.resetExplanations()
        // document.querySelector(".practice").scrollTop
        
        // find all indeces of unanswered questions
        let unansweredIndeces = []
        this.exam.answers.forEach( (e,i) => {
          if(e.answer == null){
            unansweredIndeces.push(i)
          }
        })
  
        
  
        // if there is only 1 left, do nothing.
        // if(unansweredIndeces.length == 1) return
  
        // find an index that has greater than the toIndex. 
        // if found, jump to there
        let i = unansweredIndeces.findIndex( e => e > toIndex)
        if(i == -1){
          // if nothing was found, get the first unanswered index
          // and return it
          this.exam.index = unansweredIndeces[0]
        }else{
          this.exam.index = unansweredIndeces[i]
        }
        
      },
  
      isTheLastQuestion(index){
        if(index == this.questionnaire.length-1){
          console.log("it's the last question")
          return true
        }else{
          return false
        }
      },
  
      isTheRemainingQuestion(){
        let i = this.exam.answers.findIndex(e => e.answer == null)
        let f = this.exam.answers.filter(e => e.answer == null)
        if(f.length == 0){
          console.log('this is the last remaining question')
          return true
        }else{
          return false
        }
      },
  
      isOnlyOneUnansweredLeft(){
        let filteredArray = this.exam.answers.filter(e => e.answer == null)
        // console.log(filteredArray)
        if(filteredArray.length == 1){
          return true
        }else{
          return false
        }
      },  
  
      jump(fromIndex, toIndex){
        // document.querySelector(".practice").scrollTop
        this.resetExplanations()
        console.log(`jumping from ${fromIndex} to ${toIndex}...`)
        this.exam.index = toIndex
        this.isQuestionExplorerShown = false
      },
  
      closeQExplorer(){
        this.isQuestionExplorerShown = false
      },

      /**
       * Fetch the subtopic questionnaire first. Afterwards, check if a new practice
       * exam needs to be initialized or resume an existing one
       * @NOTE: Connects to Firebase.
       */
      async getQuestionnaire(subtopicId) {

        let practiceId = this.$route.query.practiceId ? this.$route.query.practiceId : null;
        
        if (practiceId == null ) {
          // initialize new exam
          this.initPracticeExam(subtopicId);
        } else {
          // query the passed exam
          console.log('fetching id:', practiceId)
          this.fetchPracticeExam(subtopicId, practiceId);

        }
      },

      async preloadImagesFromHtml() {

        console.log('Start preloading images from questions...')
        // Initialize the loaded array with false values
        this.loaded = Array(this.questionnaire.length).fill(false);

        const preloadQuestion = async (q, index) => {
            const parser = new DOMParser();
            const doc = parser.parseFromString(q.questionHtml, 'text/html');
            const imgElements = doc.querySelectorAll('img'); // Find all <img> tags
            const imgPromises = Array.from(imgElements).map((img) => {
                return new Promise((resolve) => {
                    const image = new Image();
                    image.src = img.src;
                    image.onload = () => resolve();
                    image.onerror = () => resolve(); // Resolve even on error
                });
            });

            await Promise.all(imgPromises);
            // console.log('Completed image loading for question index:', index);
            this.loaded[index] = true;
        };

        const batchSize = 5; // Define the size of each batch
        const totalQuestions = this.questionnaire.length;

        for (let batchStart = 0; batchStart < totalQuestions; batchStart += batchSize) {

            const batchEnd = Math.min(batchStart + batchSize, totalQuestions);
            const batch = this.questionnaire.slice(batchStart, batchEnd);

            // console.log(`Processing batch: ${batchStart + 1} to ${batchEnd}`);
            // Preload the batch sequentially within the batch
            for (let i = 0; i < batch.length; i++) {
                const index = batchStart + i;
                await preloadQuestion(batch[i], index);
            }
        }

        // console.log('All questions processed:', this.loaded);
      },
  
      /**
       * Initialize the Practice Exam data and push it to the Database.
       * @NOTE: Connects to Firebase.
       */
      async initPracticeExam(subtopicId) {
        // console.log("Initializing new practice exam.");

        db.collection("questionnaires").doc(subtopicId).get()
          .then((doc) => {
            
            if (doc.exists) {

                // this.questionnaire = doc.data().questions;
                let _questionnaire = this.shuffleArray(doc.data().questions);

                // Filter to include only those with questions and exclude study-only items.
                this.questionnaire = _questionnaire.filter((q)=> q.question.trim() != '' || (q.questionHtml.trim() != '' && q.questionHtml.trim() != '<p><br></p>' && q.questionHtml.trim() !== '<p></p>'))

                this.preloadImagesFromHtml();

                // shuffle the choices
                this.questionnaire.forEach((q, i) => {
                  if (!this.questionnaire[i].choicesShuffleDisabled) {
                      this.questionnaire[i].choices = this.shuffleArray(q.choices);
                  }
                });
                
                // Create an 'answers' array with null values that matches the questionnaire size
                let answers = [];
                this.questionnaire.forEach(() => answers.push({ answer: null }));
    
                // Create the exam data model
                this.exam = {
                    user: firebase.auth().currentUser.uid,
                    questionnaire: this.questionnaire,
                    result: null,         // result of the exam. Can be passed or failed
                    score: null,          // user score
                    status: "started",    // status of the exam. Can be either started, timedout, cancelled or completed
                    subtopic: subtopicId,
                    time: {
                        start: Date.now(),  // date timestamp of when the exam was started
                        end: 0,             // date timestamp of when the exam was finished
                    },
                    index: 0,             // current index of where the user is in the exam
                    progress: 0,          // user progress
                    answers: answers,     // the answer array relative to the questionnaire.
                    url: `/plus/practice/?subtopicId=${subtopicId}&subjectId=${this.$route.query.subjectId}&subjectName=${this.$route.query.subjectName}`               // NEW: Quick store a URL
                };
                // console.log("Practice exam data during initialization => ", this.exam);
    
                // Push new practice exam data to database
                db.collection("plus_practice").add(this.exam)
                    .then((docRef) => {
                    // console.log("Practice exam data successfully pushed to the DB.");
                    
                    // Reroute the user to same Pracice Exam page but with mock exam ID passed as query
                    this.$router.replace({
                        name: "CAAP Plus Practice",
                        query: { practiceId: docRef.id, subtopicId: subtopicId, subjectId: this.$route.query.subjectId, subjectName: this.$route.query.subjectName },
                    });
                    
                    // Shuffle the choices within the questionnaire
                    this.questionnaire.forEach((q, i) => {
                      if (!this.questionnaire[i].choicesShuffleDisabled) {
                          this.questionnaire[i].choices = this.shuffleArray(q.choices);
                      }
                    });
                    
                    // User can now start the exam
                    this.isLoading = false;
    
                    })
                    .catch((error) => {
                        console.error(error)
                        console.error("Error adding new Practice Exam data to DB => ", error);
                    });
            }
          })
          .catch((error) => {
            console.error(`Fetching the ${subtopicId} questionnaire => `, error)
          });
      },
  
      /**
       * Fetch a paused Practice Exam data from the DB via practice_exam_id.
       * @NOTE: Connects to Firebase.
       */
    async fetchPracticeExam(subtopicId, practiceId) {
        // console.log("Fetching a paused practice exam");

        // Get the practice exam
        db.collection("plus_practice").doc(practiceId).get()
        .then((doc) => {
            // Check if this practice exam object has the right subtopic
            if (doc.exists && doc.data().subtopic == subtopicId) {
            this.exam = doc.data();

            // console.log(this.exam.questionnaire)

            if(this.exam.questionnaire == null){
                this.initPracticeExam(subtopicId)
            }
            
            this.questionnaire = this.exam.questionnaire
            this.preloadImagesFromHtml();
            // console.log("Practice exam data found => ", this.exam);

            // Shuffle the choices within the questionnaire
            // this.questionnaire.forEach((q, i) => {
            //   this.questionnaire[i].choices = this.shuffleArray(q.choices);
            // });

            // User can now start the exam
            this.isLoading = false;
            
            }
        })
        .catch((error) => {
            console.error("Error fetching Practice Exam object => ", error);
            this.$router.push({ path: `/plus/subtopics`, query: { subjectId: this.$route.query.subjectId, subjectName: this.$route.query.subjectName } });
        });
    },
  
      /**
       * Returns the number of running correct answers
       * @TODO: Check if can be converted into reusable library. Also used in Mock Exam.
       * -- Issue for TODO: Using this.questionnaire (Practice) instead of this.exam.questionnaire (Mock)
       */
      countCorrectAnswers() {
        var count = 0;
        this.exam.answers.forEach((e, i) => {
          if (e.answer != null && e.answer == this.questionnaire[i].answer) count++;
        });
        return count;
      },
  
      /**
       * Returns the number of running incorrect answers
       */
      countIncorrectAnswers() {
        var count = 0;
        this.exam.answers.forEach((e, i) => {
          if (e.answer != null && e.answer != this.questionnaire[i].answer) count++;
        });
        return count;
      },
  
      /**
       * Fires when a choice is selected. In this function, we do any tasks required 
       * before the user can go to the next question (or complete the practice).
       * The Next button only increments the index to move to next question.
       */
      reveal(i) {
  
        // Update the answer chosen
        this.exam.answers[this.exam.index].answer = this.questionnaire[this.exam.index].choices[i].id;
  
        // console.log('correctAnswer:', this.questionnaire[this.exam.index].answer)
        // console.log('userAnswered:', this.exam.answers[this.exam.index].answer)
        if(!this.isSoundOff){
          // make sure to pause and reload any audio playing
          this.resetAudio();
          if(this.questionnaire[this.exam.index].answer == this.exam.answers[this.exam.index].answer){
            // user answered correctly
            this.correctAudio.play()
          }else{
            this.wrongAudio.play()
          }
        }
  
        // Update the progress
        // count all answers
        let count = this.exam.answers.reduce((accumulator, currentValue) => {
            if (currentValue.answer !== null) {
                return accumulator + 1;
            } else {
                return accumulator;
            }
        }, 0);
  
        this.exam.progress = (count / this.questionnaire.length) * 100;
        this.exam.progress = this.exam.progress.toFixed(2);
        
        // Update the score
        this.exam.score = (this.countCorrectAnswers()/this.questionnaire.length)*100
      },
  
      resetAudio(){
        this.correctAudio.pause()
        this.correctAudio.currentTime = 0
        this.wrongAudio.pause()
        this.wrongAudio.currentTime = 0
      },
  
      // this will take the current question and push it to the end of the questionnaire
      moveQuestionToEnd(){
  
      },
  
      /**
       * This will check the practice exam results and status.
       * Once complete, user and leaderboard data in the database is updated.
       * @NOTE: Connects to Firebase.
       */
      completePractice() {
        // @TODO: 75% is the default passing but we must not hardcode this in the future
        this.exam.result = this.countCorrectAnswers() > (this.questionnaire.length * 0.75) ? "passed" : "failed";
        this.exam.status = "completed";
        this.exam.time.end = Date.now();
  
        // play pass or fail audio
  
        if(!this.isSoundOff){
          if(this.exam.result == 'passed'){
            this.passedAudio.pause()
            this.passedAudio.currentTime = 0
            this.passedAudio.play()
          }else if(this.exam.result == 'failed'){
            this.failedAudio.pause()
            this.failedAudio.currentTime = 0
            this.failedAudio.play()
          }
        }
  
        
        
        // Open Complete Modal. Once the user Continues to exit, practice exam data will be saved to DB
        this.openOnCompleteModal();
      },
  
      /**
       * Wraps up the user's practice exam data as he/she exits the practice exam page and saves it to DB.
       * @NOTE: Connects to Firebase.
       */
      exitPractice() {
        let practiceId = this.$route.query.practiceId

        // update the practice exam data and then redirect back to subjects page
        db.collection("plus_practice").doc(practiceId).update(this.exam)
          .then(() => {
            this.$router.push({ path: `/plus/subtopics`, query: { subjectId: this.$route.query.subjectId, subjectName: this.$route.query.subjectName } });
          })
          .catch((error) => {
            console.error("Error updating Practice Exam data => ", error);
          });
      },
  
      /**
       * Returns the corresponding HTML classes for showing correct and incorrect choices
       * @NOTE: Utility function for UI
       */
      showAnswers(i) {
        if (this.exam.answers[this.exam.index].answer == null) {
          return "";
        } else {
          return this.questionnaire[this.exam.index].answer ==
            this.questionnaire[this.exam.index].choices[i].id
            ? "text-green-500 font-bold"
            : "text-red-500";
        }
      },
  
      /**
       * Returns the corresponding HTML classes for the selected choice or otherwise
       * @NOTE: Utility function for UI
       * @TODO: Check if can be converted into reusable library. Also used in Mock Exam.
       * -- Issue for TODO: Using this.questionnaire (Practice) instead of this.exam.questionnaire (Mock)
       */
      showSelected(i) {
        return this.exam.answers[this.exam.index].answer ==
          this.questionnaire[this.exam.index].choices[i].id
          ? "bg-true-gray-100 dark:bg-true-gray-700"
          : "bg-white dark:bg-true-gray-800 hover:bg-true-gray-100 dark:hover:bg-true-gray-700";
      },
      // : "bg-white dark:bg-true-gray-800 hover:bg-true-gray-100 dark:hover:bg-true-gray-700";
  
      /**
       * Returns the HTML classes for the border of the choices container.
       * If first choice, set the top border as curved
       * If last choice, set the bottom border as curved
       * @NOTE: Utility function for UI
       * @TODO: Check if can be converted into reusable library. Also used in Practice Exam.
       * -- Issue for TODO: Using this.questionnaire (Practice) instead of this.exam.questionnaire (Mock)
       */
      setBorder(i) {
        if (i == 0) {
          return "rounded-tl-md rounded-tr-md";
        } else if (i == this.questionnaire[this.exam.index].choices.length - 1) {
          return "rounded-bl-md rounded-br-md";
        }
      },
  
      /**
       * Shuffle an array
       * @NOTE: Utility function.
       * @TODO: Convert into reusable library
       */
      shuffleArray(choices) {
        return choices.sort(() => Math.random() - 0.5);
      },
  
      resetExplanations(){
        // this.answerExplanation = '';
        // this.answerExplanationArr = [];
        this.explanation = {};
        this.alreadyVoted = false;
        this.voteType = '';
        this.isWaitingForExplanation = false;
        this.showNoExplanation = false;
      },
  
      // explainAnswer(q, c, a){
      //   this.isWaitingForExplanation = true;
      //   const ans = c.filter( e=> e.id == a.toString())[0].choice
  
      //   const params = {
      //     // prompt: `You are an Aircraft Mechanic in the Philippines. Question: "${q}". Answer: "${ans}". Explain why and show solution if math problem.`, // we should have different prompts for different topics
      //     // prompt: `You are an Aircraft Mechanic in the Philippines. Question: "${q}". Answer: "${ans}". Explain why.`,
      //     prompt: `Question: "${q}". Answer: "${ans}". Explain why in detail, with solution if possible.`, // we should have different prompts for different topics
      //     model: "text-davinci-003",
      //     max_tokens: 2000,
      //     n: 2,
      //     temperature: 0.5,
      //   };
  
      //   client
      //     .post("https://api.openai.com/v1/completions", params)
      //     .then((result) => {
      //       if(this.isWaitingForExplanation){
      //         this.answerExplanation = result.data.choices[0].text;
      //         this.answerExplanation = this.answerExplanation.replace(/(?:\r\n|\r|\n)/g, '\n\n');
      //         this.answerExplanationArr = this.answerExplanation.split(/(?:\r\n|\r|\n)/g).filter(item => item !== '');
              
      //         this.isWaitingForExplanation = false;
      //       }
            
              
      //     })
      //     .catch((err) => {
      //       console.log(err);
      //       this.isWaitingForExplanation = false;
      //     });
      // },
  
  
      /***
       * EXPLANATION v2
       */
      async explainAnswer(subtopic, questionnaire){
        this.isWaitingForExplanation = true;
  
        let uniqueQuestionId = `${subtopic}_${questionnaire.id}`
        console.log('UNIQUE ID:', uniqueQuestionId)
  
        // fetch the explanatinos collections if it already exists.
        // if yes, then display this explanation
        // if not, fetch for an explanation from OpenAI and then save it
        // store explanation in UI and display
  
        console.log("Checking for any stored explanation.")
        await db.collection("explanations").where("questionId", "==", uniqueQuestionId)
          .get()
          .then((querySnapshot) => {
            if(querySnapshot.size > 0){ 
              console.log("A stored explanation was found.")
  
              // this.answerExplanation = querySnapshot.docs[0].data().text
              // this.answerExplanation = this.answerExplanation.replace(/(?:\r\n|\r|\n)/g, '\n\n');
              // this.answerExplanationArr = this.answerExplanation.split(/(?:\r\n|\r|\n)/g).filter(item => item !== '');
  
              this.explanation = {
                id: querySnapshot.docs[0].id,
                data: querySnapshot.docs[0].data()
              }
  
              this.isWaitingForExplanation = false;
            }else{
              console.log("A stored explanation was NOT found.")
              // this.doItWithAi(questionnaire, uniqueQuestionId)
              this.isWaitingForExplanation = false;
              this.showNoExplanation = true;
            }
              
          })
          .catch((error) => {
              console.log("Error getting documents: ", error);
          });
        
      },
  
      formatExplanationForViewing(rawExplanation){
        let processedExplanation = null;
        processedExplanation = rawExplanation.replace(/(?:\r\n|\r|\n)/g, '\n\n');
        return processedExplanation.split(/(?:\r\n|\r|\n)/g).filter(item => item !== '');
      },
  
      async doItWithAi (questionnaire, uniqueQuestionId) {
        console.log("Fetching explanation from AI.")
        let result = await this.fetchExplanationFromOpenAi(questionnaire.question, questionnaire.choices, questionnaire.answer)
        if(!result.isSuccess) return
        
        console.log("AI returned an explanation", result.data)
  
        // Attempt to save the explanation
        let payload = {
          questionId: uniqueQuestionId,
          text: result.data,
          upvotes: 0,
          downvotes: 0
        }
        console.log("Storing explanation to DB: ", payload)
        await this.storeExplanation(payload)
  
        this.isWaitingForExplanation = false
      },
  
      async storeExplanation(payload){
        try {
          const result = await upsertExplanationByQuestionId(payload, { preserveVotes: true })
          if (result && result.id) {
            this.explanation = {
              id: result.id,
              data: payload
            }
            return true
          }
          return false
        } catch (error) {
          console.error("Error saving explanation: ", error)
        }  
      },
  
      async fetchExplanationFromOpenAi(q, c, a){
  
        const ans = c.filter( e=> e.id == a.toString())[0].choiceHtml
        
        let htmlStrippedQuestion = q.replace(/(<([^>]+)>)/gi, "");
        let htmlStrippedAnswer = ans.replace(/(<([^>]+)>)/gi, "");
  
        const params = {
          // prompt: `You are an Aircraft Mechanic in the Philippines. Question: "${q}". Answer: "${ans}". Explain why and show solution if math problem.`, // we should have different prompts for different topics
          // prompt: `You are an Aircraft Mechanic in the Philippines. Question: "${q}". Answer: "${ans}". Explain why.`,
          prompt: `Question: "${htmlStrippedQuestion}". Answer: "${htmlStrippedAnswer}". Explain why in detail, with solution if possible.`, // we should have different prompts for different topics
          model: "text-davinci-003",
          max_tokens: 2000,
          n: 2,
          temperature: 0.5,
        };
  
        try {
          let result = await client.post("https://api.openai.com/v1/completions", params)
          // console.log(result)
          if(this.isWaitingForExplanation){
              return {
                isSuccess: true,
                data: result.data.choices[0].text
              }
            }
        } catch (error) {
          console.error(error);
            this.isWaitingForExplanation = false;
            return {
              isSuccess: false
            }
        }
          
      },
  
      async sendFeedBack(voteType) {
        
        // set the data
        let data = {}
        if(voteType == 'upvote'){
          if(this.explanation.data.upvotes){
            this.explanation.data.upvotes++;
          }else{
            this.explanation.data.upvotes = 1;
          }
          data = { upvotes: this.explanation.data.upvotes }
        }else if(voteType == 'downvote'){
          if(this.explanation.data.downvotes){
            this.explanation.data.downvotes++;
          }else{
            this.explanation.data.downvotes = 1;
          }
          data = { downvotes: this.explanation.data.downvotes }
        }
  
        // update the UI first. It's okay to let the saving get delayed. Non-crucial data.
        this.alreadyVoted = true;
        this.voteType = voteType;
  
        // save the votes
        await db.collection("explanations").doc(this.explanation.id)
          .update(data)
          .then(res => {
            console.log('updating vote done.')
            console.log('updating vote res.')
          })
          .catch((error) => {
              console.error("Error getting documents: ", error);
          });
      },
  
    },
  
    mounted() {
  
      let reviewerId = 'plus';
  
      // If a user is logged in, initialize the app
      if (firebase.auth().currentUser) {

        let subtopicId = this.$route.query.subtopicId;

        if(!subtopicId){
          console.error("Subtopic is null")
          this.$router.push({ path: `/plus/subjects`});
        } 
          
        // Get the Subtopic name and store it 
        // @TODO: Find a way to be able to remove this.
        db.collection("reviewers").doc(reviewerId).collection("subtopics").doc(subtopicId).get()
          .then((doc) => { if (doc.exists) this.subtopic_name = doc.data().name })
          .catch((error) => { console.error("Error fetching subtopic data => ", error) });
  
        // Get the questionnaire and take it from there
        this.getQuestionnaire(subtopicId);
  
      }
    },
  
    created() {
  
      /* setup audio */
      this.correctAudio = new Audio(correctSound)
      this.wrongAudio = new Audio(wrongSound)
      this.passedAudio = new Audio(passedSound)
      this.failedAudio = new Audio(failedSound)
      this.wrongAudio.volume = 0.2
      this.correctAudio.volume = 0.2
      this.passedAudio.volume = 0.2
      this.failedAudio.volume = 0.2
  
      // document.querySelector("body").scrollTop
      attachLeaveGuard();
  
    },

    beforeUnmount() {
      detachLeaveGuard();
    },
  
  };
  
  /**
   * Refactored for v0.1.0 on 11/12/2021
   */
  
  </script>